<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            <strong>Google Street View</strong> permet à vos clients de visiter
            votre établissement en ligne avant de franchir la porte. Lorsque
            vos pièces apparaissent en panoramas 360° reliés sur Google Maps et
            votre fiche Google Business Profile, les internautes explorent vos
            locaux depuis la Recherche, Maps ou l’
            <strong>application Street View</strong>.
          </p>
          <p>
            Avec Travvir, vous publiez depuis l’app — sans compte Google
            personnel. Les images sont placées sur <em>votre</em> lieu Google
            Maps ; <strong>Travvir est l’éditeur Street View</strong> de cet
            emplacement.
          </p>
          <p>
            Deux parcours. Pour un <strong>scan unique</strong>, choisissez le
            lieu sur Google Maps, sélectionnez les scènes, puis publiez-les sur
            Street View. Pour une <strong>visite 360°</strong>, choisissez le
            lieu sur Google Maps et publiez la visite liée directement.
          </p>
        </section>

        <section>
          <h2>Points clés</h2>
          <ul>
            <li>
              Street View pour les entreprises = panoramas 360° intérieurs reliés
              (« Voir l’intérieur »).
            </li>
            <li>
              Travvir est l’éditeur Street View sur votre lieu Maps — pas de
              connexion à votre compte Google perso dans l’app.
            </li>
            <li>
              Scan unique : lieu Maps, choix des scènes, publication. Visite
              360° : lieu Maps, puis publication directe de la visite.
            </li>
            <li>
              Les hotspots Travvir restent sur votre tour hébergé ; sur Google,
              seuls les panoramas et les flèches de navigation apparaissent.
            </li>
          </ul>
        </section>

        <section>
          <h2>Comment ça marche aujourd’hui</h2>
          <p>
            Vous choisissez d’abord le <strong>lieu Google Maps</strong> (nom et
            pin). Travvir envoie les panoramas via son compte éditeur autorisé.
            Sur Street View, <strong>Travvir apparaît comme éditeur</strong> de
            cet emplacement ; la couverture reste à votre adresse. Google valide
            les images avant mise en ligne.
          </p>
        </section>

        <section>
          <h2>Publier avec Travvir</h2>
          <p>
            Capturez sur smartphone, puis Partager → Google Street View. Le même
            flux est décrit sur la
            <a :href="`${mainSiteUrl}/google-streetview`"
              >page Google Street View</a
            >.
          </p>
          <h3>Scan unique</h3>
          <p>
            Choisissez le lieu sur Google Maps, sélectionnez les scènes 360°,
            puis publiez-les sur Street View. Uniquement des panoramas complets ;
            le pin doit correspondre au bon bâtiment.
          </p>
          <h3>Visite 360°</h3>
          <p>
            Choisissez le lieu sur Google Maps et publiez la visite liée
            directement — sans téléverser chaque pièce à la main.
          </p>
        </section>

        <section>
          <h2>Exigences techniques</h2>
          <ul>
            <li>JPEG équirectangulaire 360° (pas d’images 2D).</li>
            <li>Résolution conforme aux minimums Google (voir notre guide taille d’image 360°).</li>
            <li>
              Nom et lieu Maps exacts. Travvir est l’éditeur Street View de cet
              emplacement.
            </li>
          </ul>
        </section>

        <section>
          <h2>App Street View ou Travvir ?</h2>
          <p>
            L’<strong>application Street View</strong> gratuite convient pour une
            sphère rapide. Travvir est idéal pour plusieurs pièces, la qualité IA
            et la publication du tour complet en un flux, tout en gardant une
            visite enrichie (hotspots) sur votre site.
          </p>
        </section>

        <section class="continue-reading">
          <h2>Continuer la lecture</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View avec Travvir</a
              >
            </li>
            <li>
              <a href="/blog/fr/visite-virtuelle-google-street-view"
                >Visite virtuelle Google Street View</a
              >
            </li>
            <li>
              <a href="/blog/fr/guide-google-maps-360">Guide Google Maps 360</a>
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find(
  (p) => p.slug === "publier-google-street-view",
);
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title:
    "Google Street View : publier un tour 360° depuis Travvir [2026] | Travvir",
  description:
    "Publiez un scan unique ou une visite 360° sur Google Street View : choisissez le lieu Maps ; Travvir est l’éditeur de cet emplacement.",
  keywords:
    "google street view, google maps 360, application street view, publier visite virtuelle google maps",
  slug: "publier-google-street-view",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Comment publier un scan unique sur Google Street View ?",
      answer:
        "Choisissez le lieu Google Maps, sélectionnez les scènes 360°, puis publiez. Travvir est l’éditeur Street View de cet emplacement ; Google valide les images avant mise en ligne.",
    },
    {
      question: "Comment publier une visite 360° ?",
      answer:
        "Choisissez le lieu Google Maps et publiez la visite liée directement depuis Travvir. Travvir apparaît comme éditeur sur ce lieu Maps.",
    },
  ],
});
</script>
